/**
 * Commission API
 *
 * API functions for:
 * - Agent expenses (CRUD, approval, recoupment)
 * - Agency commission settings
 * - Agent commission splits
 * - Agent quotas
 * - Commission ledger
 * - Summary calculations
 */

import { pb, escapeFilterValue } from '../lib/pocketbase'
import type {
  AgentExpense,
  AgentExpensePayload,
  AgencyCommissionSettings,
  AgencyCommissionSettingsPayload,
  AgentCommissionSplit,
  AgentCommissionSplitPayload,
  AgentQuota,
  AgentQuotaPayload,
  CommissionLedgerEntry,
  CommissionLedgerPayload,
  ExpenseStatus,
  QuotaStatus,
} from '../types/commission'
import { catch404 } from '../utils/apiErrorHandler'
import { localDateString } from '../utils/formatters'

// ============ AGENT EXPENSES ============

/**
 * List agent expenses with optional filters
 */
export async function listAgentExpenses(options?: {
  agentId?: string
  clientId?: string
  status?: ExpenseStatus
  startDate?: string
  endDate?: string
}): Promise<AgentExpense[]> {
  try {
    const user = pb.authStore.model
    const agencyId = (user as any)?.agency_id

    if (!agencyId) return []

    const filters: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

    if (options?.agentId) {
      filters.push(`agent_id = "${escapeFilterValue(options.agentId)}"`)
    }
    if (options?.clientId) {
      filters.push(`client_id = "${escapeFilterValue(options.clientId)}"`)
    }
    if (options?.status) {
      filters.push(`status = "${escapeFilterValue(options.status)}"`)
    }
    if (options?.startDate) {
      filters.push(`expense_date >= "${options.startDate}"`)
    }
    if (options?.endDate) {
      filters.push(`expense_date <= "${options.endDate}"`)
    }

    const records = await pb.collection('agent_expenses').getFullList({
      filter: filters.join(' && '),
      sort: '-expense_date',
      expand: 'agent_id,client_id,approved_by',
    })

    return records as unknown as AgentExpense[]
  } catch (err: any) {
    if (err?.status === 404) return []
    throw err
  }
}

/**
 * Get a single expense
 */
export async function getAgentExpense(expenseId: string): Promise<AgentExpense | null> {
  return catch404(
    pb.collection('agent_expenses').getOne(expenseId, {
      expand: 'agent_id,client_id,approved_by',
    })
  ) as Promise<AgentExpense | null>
}

/**
 * Create a new expense
 */
export async function createAgentExpense(payload: AgentExpensePayload): Promise<AgentExpense> {
  const record = await pb.collection('agent_expenses').create({
    ...payload,
    status: payload.status || 'pending',
  })
  return record as unknown as AgentExpense
}

/**
 * Update an expense
 */
export async function updateAgentExpense(
  expenseId: string,
  payload: Partial<AgentExpensePayload>
): Promise<AgentExpense> {
  const record = await pb.collection('agent_expenses').update(expenseId, payload)
  return record as unknown as AgentExpense
}

/**
 * Delete an expense
 */
export async function deleteAgentExpense(expenseId: string): Promise<void> {
  await pb.collection('agent_expenses').delete(expenseId)
}

/**
 * Approve an expense
 */
export async function approveExpense(expenseId: string): Promise<AgentExpense> {
  const user = pb.authStore.model
  return updateAgentExpense(expenseId, {
    status: 'approved',
    approved_by: user?.id,
    approved_at: new Date().toISOString(),
  })
}

/**
 * Reject an expense
 */
export async function rejectExpense(expenseId: string, reason?: string): Promise<AgentExpense> {
  const user = pb.authStore.model
  return updateAgentExpense(expenseId, {
    status: 'rejected',
    approved_by: user?.id,
    approved_at: new Date().toISOString(),
    notes: reason || undefined,
  })
}

/**
 * Mark expense as recouped from a deal
 */
export async function recoupExpenseFromDeal(
  expenseId: string,
  dealId: string,
  amount: number
): Promise<AgentExpense> {
  const expense = await getAgentExpense(expenseId)
  if (!expense) throw new Error('Expense not found')

  const currentRecouped = expense.recouped_amount || 0
  const newRecouped = currentRecouped + amount
  const fullyRecouped = newRecouped >= expense.amount

  return updateAgentExpense(expenseId, {
    recouped_amount: newRecouped,
    recouped_from_deal_id: dealId,
    recouped_date: localDateString(),
    status: fullyRecouped ? 'recouped' : 'partial',
  })
}

/**
 * Get unrecouped expenses for an agent (optionally by client)
 */
export async function getUnrecoupedExpenses(
  agentId: string,
  clientId?: string
): Promise<AgentExpense[]> {
  return listAgentExpenses({
    agentId,
    clientId,
  }).then(expenses =>
    expenses.filter(e =>
      (e.status === 'approved' || e.status === 'partial') &&
      (e.recouped_amount || 0) < e.amount
    )
  )
}

// ============ AGENCY COMMISSION SETTINGS ============

/**
 * Get agency commission settings
 */
export async function getAgencyCommissionSettings(
  agencyId?: string
): Promise<AgencyCommissionSettings | null> {
  const user = pb.authStore.model
  const id = agencyId || (user as any)?.agency_id

  if (!id) return null

  const records = await pb.collection('agency_commission_settings').getFullList({
    filter: `agency_id = "${escapeFilterValue(id)}"`,
  })

  return records[0] as unknown as AgencyCommissionSettings || null
}

/**
 * Create or update agency commission settings
 */
export async function saveAgencyCommissionSettings(
  payload: AgencyCommissionSettingsPayload
): Promise<AgencyCommissionSettings> {
  // Check if settings already exist
  const existing = await getAgencyCommissionSettings(payload.agency_id)

  if (existing) {
    const record = await pb.collection('agency_commission_settings').update(existing.id, payload)
    return record as unknown as AgencyCommissionSettings
  }

  const record = await pb.collection('agency_commission_settings').create(payload)
  return record as unknown as AgencyCommissionSettings
}

// ============ AGENT COMMISSION SPLITS ============

/**
 * Get current commission split for an agent
 */
export async function getAgentCommissionSplit(
  agentId: string
): Promise<AgentCommissionSplit | null> {
  const agencyId = (pb.authStore.model as any)?.agency_id
  if (!agencyId) return null

  const today = new Date().toISOString().split('T')[0]

  const records = await pb.collection('agent_commission_splits').getFullList({
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && agent_id = "${escapeFilterValue(agentId)}" && effective_date <= "${today}" && (end_date = "" || end_date >= "${today}")`,
    sort: '-effective_date',
    expand: 'agent_id,approved_by',
  })

  return records[0] as unknown as AgentCommissionSplit || null
}

/**
 * List all splits for an agent (including historical)
 */
export async function listAgentCommissionSplits(
  agentId: string
): Promise<AgentCommissionSplit[]> {
  const agencyId = (pb.authStore.model as any)?.agency_id
  if (!agencyId) return []

  const records = await pb.collection('agent_commission_splits').getFullList({
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && agent_id = "${escapeFilterValue(agentId)}"`,
    sort: '-effective_date',
    expand: 'agent_id,approved_by',
  })

  return records as unknown as AgentCommissionSplit[]
}

/**
 * Create a new commission split for an agent
 */
export async function createAgentCommissionSplit(
  payload: AgentCommissionSplitPayload
): Promise<AgentCommissionSplit> {
  const user = pb.authStore.model

  // End any existing current split
  const currentSplit = await getAgentCommissionSplit(payload.agent_id)
  if (currentSplit && !currentSplit.end_date) {
    const dayBefore = new Date(payload.effective_date)
    dayBefore.setDate(dayBefore.getDate() - 1)
    await pb.collection('agent_commission_splits').update(currentSplit.id, {
      end_date: dayBefore.toISOString().split('T')[0],
    })
  }

  const record = await pb.collection('agent_commission_splits').create({
    ...payload,
    approved_by: user?.id,
  })

  return record as unknown as AgentCommissionSplit
}

// ============ AGENT QUOTAS ============

/**
 * List quotas for an agent
 */
export async function listAgentQuotas(options?: {
  agentId?: string
  status?: QuotaStatus
  periodType?: string
}): Promise<AgentQuota[]> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) return []

  const filters: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

  if (options?.agentId) {
    filters.push(`agent_id = "${escapeFilterValue(options.agentId)}"`)
  }
  if (options?.status) {
    filters.push(`status = "${escapeFilterValue(options.status)}"`)
  }
  if (options?.periodType) {
    filters.push(`period_type = "${escapeFilterValue(options.periodType)}"`)
  }

  const records = await pb.collection('agent_quotas').getFullList({
    filter: filters.join(' && '),
    sort: '-period_start',
    expand: 'agent_id',
  })

  return records as unknown as AgentQuota[]
}

/**
 * Get a single quota
 */
export async function getAgentQuota(quotaId: string): Promise<AgentQuota | null> {
  return catch404(
    pb.collection('agent_quotas').getOne(quotaId, {
      expand: 'agent_id',
    })
  ) as Promise<AgentQuota | null>
}

/**
 * Create a new quota
 */
export async function createAgentQuota(payload: AgentQuotaPayload): Promise<AgentQuota> {
  const record = await pb.collection('agent_quotas').create({
    ...payload,
    status: 'active',
    current_progress: 0,
  })
  return record as unknown as AgentQuota
}

/**
 * Update quota progress
 */
export async function updateQuotaProgress(
  quotaId: string,
  progress: number,
  status?: QuotaStatus
): Promise<AgentQuota> {
  const record = await pb.collection('agent_quotas').update(quotaId, {
    current_progress: progress,
    status: status || undefined,
  })
  return record as unknown as AgentQuota
}

// ============ COMMISSION LEDGER ============

/**
 * List ledger entries
 */
export async function listCommissionLedger(options?: {
  agentId?: string
  dealId?: string
  contractId?: string
  transactionType?: string
  startDate?: string
  endDate?: string
}): Promise<CommissionLedgerEntry[]> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) return []

  const filters: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

  if (options?.agentId) {
    filters.push(`agent_id = "${escapeFilterValue(options.agentId)}"`)
  }
  if (options?.dealId) {
    filters.push(`deal_id = "${escapeFilterValue(options.dealId)}"`)
  }
  if (options?.contractId) {
    filters.push(`contract_id = "${escapeFilterValue(options.contractId)}"`)
  }
  if (options?.transactionType) {
    filters.push(`transaction_type = "${escapeFilterValue(options.transactionType)}"`)
  }
  if (options?.startDate) {
    filters.push(`transaction_date >= "${options.startDate}"`)
  }
  if (options?.endDate) {
    filters.push(`transaction_date <= "${options.endDate}"`)
  }

  const records = await pb.collection('commission_ledger').getFullList({
    filter: filters.join(' && '),
    sort: '-transaction_date',
    expand: 'agent_id,deal_id,client_id',
  })

  return records as unknown as CommissionLedgerEntry[]
}

/**
 * Create a ledger entry
 */
export async function createCommissionLedgerEntry(
  payload: CommissionLedgerPayload
): Promise<CommissionLedgerEntry> {
  const record = await pb.collection('commission_ledger').create(payload)
  return record as unknown as CommissionLedgerEntry
}

/**
 * Record commission earned from a deal
 */
export async function recordCommissionEarned(
  dealId: string,
  agentId: string,
  grossAmount: number,
  expensesDeducted: number,
  netAmount: number,
  agentShare: number,
  agencyShare: number,
  splitPercentage: number,
  clientId?: string
): Promise<CommissionLedgerEntry> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  return createCommissionLedgerEntry({
    agent_id: agentId,
    agency_id: agencyId,
    deal_id: dealId,
    client_id: clientId || null,
    contract_id: null,
    transaction_type: 'commission_earned',
    gross_amount: grossAmount,
    expenses_deducted: expensesDeducted,
    net_amount: netAmount,
    agent_share: agentShare,
    agency_share: agencyShare,
    split_percentage: splitPercentage,
    transaction_date: localDateString(),
    period: `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    notes: null,
  })
}

/**
 * Record commission earned from a contract activation
 */
export async function recordContractCommissionEarned(
  contractId: string,
  agentId: string,
  grossAmount: number,
  expensesDeducted: number,
  netAmount: number,
  agentShare: number,
  agencyShare: number,
  splitPercentage: number,
  clientId?: string,
  dealId?: string | null,
): Promise<CommissionLedgerEntry> {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  return createCommissionLedgerEntry({
    agent_id: agentId,
    agency_id: agencyId,
    deal_id: dealId || null,
    client_id: clientId || null,
    contract_id: contractId,
    transaction_type: 'commission_earned',
    gross_amount: grossAmount,
    expenses_deducted: expensesDeducted,
    net_amount: netAmount,
    agent_share: agentShare,
    agency_share: agencyShare,
    split_percentage: splitPercentage,
    transaction_date: localDateString(),
    period: `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    notes: null,
  })
}

// ============ SUMMARY FUNCTIONS ============

/**
 * Get commission dashboard data for an agent
 */
export async function getCommissionDashboardData(
  agentId: string,
  startDate: string,
  endDate: string
) {
  const [expensesResult, ledgerResult, quotasResult, settingsResult, splitResult] =
    await Promise.allSettled([
      listAgentExpenses({ agentId, startDate, endDate }),
      listCommissionLedger({ agentId, startDate, endDate }),
      listAgentQuotas({ agentId, status: 'active' }),
      getAgencyCommissionSettings(),
      getAgentCommissionSplit(agentId),
    ])

  // Primary data — throw if failed
  if (expensesResult.status === 'rejected') throw expensesResult.reason
  if (ledgerResult.status === 'rejected') throw ledgerResult.reason

  const expenses = expensesResult.value
  const ledger = ledgerResult.value
  const quotas = quotasResult.status === 'fulfilled' ? quotasResult.value : []
  const agencySettings = settingsResult.status === 'fulfilled' ? settingsResult.value : null
  const agentSplit = splitResult.status === 'fulfilled' ? splitResult.value : null

  // Calculate totals from ledger
  const commissionEntries = ledger.filter(e => e.transaction_type === 'commission_earned')

  const grossEarned = commissionEntries.reduce((sum, e) => sum + (e.gross_amount || 0), 0)
  const expensesDeducted = commissionEntries.reduce((sum, e) => sum + (e.expenses_deducted || 0), 0)
  const netCommission = commissionEntries.reduce((sum, e) => sum + (e.net_amount || 0), 0)
  const agentTake = commissionEntries.reduce((sum, e) => sum + (e.agent_share || 0), 0)
  const agencyTake = commissionEntries.reduce((sum, e) => sum + (e.agency_share || 0), 0)

  // Calculate expenses
  const expensesAdvanced = expenses.reduce((sum, e) => sum + e.amount, 0)
  const expensesRecouped = expenses
    .filter(e => e.status === 'recouped')
    .reduce((sum, e) => sum + (e.recouped_amount || e.amount), 0)
  const expensesPending = expenses
    .filter(e => e.status === 'pending')
    .reduce((sum, e) => sum + e.amount, 0)

  // Get current split
  const _currentSplit = agentSplit || agencySettings
  // The split on file, or none. A 0% split is a real setting; nothing is invented.
  const splitPercentage: number | null = agentSplit?.agent_percentage ?? agencySettings?.default_agent_split ?? null

  return {
    period: { startDate, endDate },
    summary: {
      grossEarned,
      expensesDeducted,
      netCommission,
      agentTake,
      agencyTake,
      expensesAdvanced,
      expensesRecouped,
      expensesPending,
    },
    split: {
      agentPercentage: splitPercentage,
      agencyPercentage: splitPercentage == null ? null : 100 - splitPercentage,
    },
    quotas: quotas.map(q => ({
      ...q,
      percentage: q.target_amount > 0 ? ((q.current_progress || 0) / q.target_amount) * 100 : 0,
    })),
    expenses,
    ledger,
    settings: agencySettings,
  }
}

/**
 * Change the amounts on a ledger entry. Used when a commission is
 * reconciled to what the athlete has actually earned; the row keeps its
 * identity and date so history stays in one place.
 */
export async function updateCommissionLedgerEntry(
  entryId: string,
  patch: Partial<Pick<CommissionLedgerPayload, 'gross_amount' | 'expenses_deducted' | 'net_amount' | 'agent_share' | 'agency_share' | 'notes'>>,
): Promise<CommissionLedgerEntry> {
  const record = await pb.collection('commission_ledger').update(entryId, patch)
  return record as unknown as CommissionLedgerEntry
}
